import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Scale, Hammer, Archive, Sparkles, Ship, Radio, Crown, Menu, X, Anchor, Store, Target } from "lucide-react";
import SovereignAssistant from "@/components/SovereignAssistant";

const navItems = [
  { to: "/bridge", label: "Bridge", icon: Crown, testid: "nav-bridge" },
  { to: "/forge", label: "Forge", icon: Hammer, testid: "nav-forge" },
  { to: "/archives", label: "Archives", icon: Archive, testid: "nav-archives" },
  { to: "/fleet", label: "Fleet", icon: Ship, testid: "nav-fleet" },
  { to: "/stream", label: "Stream", icon: Radio, testid: "nav-stream" },
  { to: "/public-forge", label: "Public", icon: Sparkles, testid: "nav-public-forge" },
];

const ALLIANCE_LINKS = [
  {
    name: "Island Gold",
    href: "https://eigoo-command.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=flagship",
    icon: Anchor,
    testid: "alliance-island-gold",
    sub: "Flagship",
  },
  {
    name: "Siren Hub",
    href: "https://siren-storefront.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=siren",
    icon: Store,
    testid: "alliance-siren-hub",
    sub: "Commerce",
  },
  {
    name: "Lead Sentry",
    href: "https://lead-sentry-gold.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=leadsentry",
    icon: Target,
    testid: "alliance-lead-sentry",
    sub: "Lead-Gen",
  },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header
        className="sticky top-0 z-50 glass-strong"
        style={{ paddingTop: "env(safe-area-inset-top, 0)" }}
        data-testid="primary-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="brand-link">
            <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center border border-[#C9A227]/40 group-hover:border-[#C9A227] transition-colors">
              <Scale className="w-5 h-5 gold-text" strokeWidth={1.5} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-[10px] label-tag">EIGOO Armada</div>
              <div className="text-sm font-bold tracking-[0.18em] uppercase truncate">Sovereign Legal</div>
            </div>
          </Link>

          {/* Desktop nav — visible at lg breakpoint (1024px+) */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold transition-all flex items-center gap-2 ${
                      isActive
                        ? "text-[#C9A227] border-b border-[#C9A227]"
                        : "text-gray-400 hover:text-white border-b border-transparent"
                    }`
                  }
                  end={item.to === "/"}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <span className="hidden xl:inline-flex items-center text-[9px] font-bold tracking-[0.2em] uppercase text-[#C9A227]/80 border border-[#C9A227]/30 px-2 py-1 whitespace-nowrap" data-testid="header-eigoo-badge">
              Empowered by EIGOO Inc
            </span>
            <span className="text-[10px] label-tag">legal.eigooinc.net</span>
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          </div>

          {/* Mobile hamburger — visible below lg */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#C9A227]/40 hover:border-[#C9A227] hover:bg-[#C9A227]/5 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen
              ? <X className="w-5 h-5 gold-text" strokeWidth={1.8} />
              : <Menu className="w-5 h-5 gold-text" strokeWidth={1.8} />}
          </button>
        </div>

        {/* Mobile slide-down panel */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
            data-testid="mobile-nav-panel"
          >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeMobile}
                    data-testid={`mobile-${item.testid}`}
                    className={({ isActive }) =>
                      `px-4 py-3 text-sm uppercase tracking-[0.18em] font-bold transition-colors flex items-center gap-3 border-l-2 ${
                        isActive
                          ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/5"
                          : "border-transparent text-gray-400 hover:text-white hover:border-white/30"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                    {item.label}
                  </NavLink>
                );
              })}
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 px-4">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-[10px] label-tag">legal.eigooinc.net</span>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10"
        style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}
      >
        {children}
      </main>

      {/* Fleet Alliance signal-links */}
      <section className="border-t border-white/10 bg-black/40" data-testid="fleet-alliance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="flex items-center gap-3 mb-5">
            <Ship className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <span className="label-tag">Fleet Alliance</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ALLIANCE_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={link.testid}
                  className="card-sharp p-4 flex items-center gap-3 hover:gold-glow transition-all group"
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C9A227]/30 group-hover:border-[#C9A227] transition-colors flex-shrink-0">
                    <Icon className="w-4 h-4 gold-text" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{link.name}</div>
                    <div className="text-[10px] label-tag">{link.sub} · alliance signal</div>
                  </div>
                  <span className="text-[10px] label-tag gold-text">→</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-gray-500">
            <Scale className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em]">Sovereign Legal · Iron Crown · v1.0</span>
          </div>
          <div
            className="inline-flex items-center gap-2 border border-[#C9A227]/40 px-3 py-1.5"
            data-testid="footer-eigoo-badge"
          >
            <Crown className="w-3.5 h-3.5 gold-text" strokeWidth={1.8} />
            <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C9A227]">
              Empowered by EIGOO Inc
            </span>
          </div>
        </div>
      </footer>

      {/* Sovereign Assistant — floating bubble on every page */}
      <SovereignAssistant />
    </div>
  );
}
